<template>
    <div class="">
        <h1 class="sr-only">{{ $t('home.srTitle') }}</h1>

        <Hero class="pt-28 md:pt-44 2xl:pt-52" />

        <!-- partners  -->
        <div class="pt-2 md:pt-4">
          <h3 class="mb-4 text-5xl text-center md:mb-6 md:text-6xl 2xl:text-7xl 2xl:mb-10 font-gt-bold">{{ $t('home.partners') }}</h3>
          <LazyHeroPartners hydrate-on-idle />
        </div>

        <div class="py-10 mt-20 2xl:mb-10 2xl:mt-24 border-y-[2px] border-dashed inside_padding  border-primary-light">
            <h3 class="pt-2 text-4xl text-center lg:text-6xl custom_title font-pp">{{ $t('home.workflowsTitle') }}</h3>
        </div>

        <!-- products spotlight : segha + zaagl + sponge click -->
        <div class="pt-16 md:pt-24">
          <div class="pb-4 mx-3 md:mx-10">
            <Title :title="$t('home.ourProducts')" :subTitle="$t('home.productsCount')" />
          </div>
          <LazyHeroProducts hydrate-on-idle />
        </div>

        <!-- work  -->
        <!-- title  -->
        <div class="py-10 mx-3 md:mx-10 ">
          <Title :title="$t('home.ourProjects')" :subTitle="$t('home.projectsPeriod')" />
        </div>
        
        <!-- projects  -->
        <div class="py-10 mx-3 md:py-16 md:mx-10 2xl:py-20">
          <div class="" ref="section">
            <h3 class="md:text-[2.5rem] text-[1.95rem] text-white font-pp">{{ $t('home.newProjectsTitle') }}</h3>
            <div class="md:pl-10 lg:pl-20 rtl:md:pl-0 rtl:md:pr-10 rtl:lg:pr-20 mt-8 space-y-4 text-lg md:w-[80%] lg:w-1/2">
              
              <p>{{ $t('home.introP1', { brand: $t('home.brand') }) }}</p>
              <p>{{ $t('home.introP2') }}</p>
            </div>
          </div>

          <div  class="flex items-end justify-end w-full pb-6 mt-14 md:mt-24">
            <div class="flex gap-8">
              <!-- fixed width + tabular digits so counting 0 → 107 does not resize the box (layout shift) -->
              <span class="inline-block min-w-[3ch] text-end tabular-nums text-[80px] md:text-[250px] lg:text-[300px] pt-6 font-pp-bolder leading-none">{{ number }}</span>
              <span class="">{{ $t('common.project') }}</span>
            </div>
          </div>

          <!-- projects  -->
          <LazyHeroProjects class="hidden lg:block" hydrate-on-idle />
          <LazyHeroProjectsSmallScreens class="lg:hidden" hydrate-on-idle />
        </div>

        <!-- title  -->
        <div class="px-2 py-6 md:py-10 md:mx-10">
          <Title :title="$t('home.clientsTitle')" :subTitle="$t('home.clientsCount')" />
        </div>
        
        <!-- clients -->
        <div class="px-2 py-6 md:py-10 md:mx-10">
          <LazyHeroClients hydrate-on-idle />
        </div>
        
        
        <!-- before footer -->
        
        <div class="relative pt-20 overflow-hidden">
          <div class="w-full masked-divider bg-primary h-[1px]"></div>
          <div ref="threeSection" class=" doted_bg h-[300px] xs:h-[350px] md:h-[500px]   relative grid place-content-center">
            
            <div class="mx-10 ">
              <div class="absolute z-[1]   inset-0 grid place-content-center">
                <ThreeLogo v-if="showThree" class="scale-[0.55] sm:scale-[0.75] md:scale-[0.85] lg:scale-[1] xl:scale-[1.15] 2xl:scale-[0.9]" />
              </div>
              
              <div class=" absolute  z-[2] inset-0 grid place-content-center ">
                    <NuxtLinkLocale to="/contact/" class=" text-[50px] xs:text-[65px] 2xl:text-[200px] md:text-[100px] lg:text-[150px] reveal relative pb-8 text-center leading-none transition duration-200 lg:hover:text-dark font-gt-bold">
                      <span class="">[</span> 
                      <span class="">{{ $t('home.contactUs') }}</span> 
                      <span class="">]</span>
                    </NuxtLinkLocale>
              </div>
            </div> 
            
          </div>
        </div>
          
          
    </div>
  </template>
  <script setup>
  import { ref, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue'
  import Hero from '~/components/Hero.vue'
  import Title from '~/components/Title.vue'

  const { t } = useI18n()
  usePreloadFont('PP-Formula-ExtraBold')
  // Below-the-fold sections are server-rendered and hydrate when the browser is idle (hydrate-on-idle).
  // The three.js logo (~550 KB) is only fetched when its section comes near the viewport.
  const ThreeLogo = defineAsyncComponent(() => import('~/components/ThreeLogo.client.vue'))

  const config = useRuntimeConfig()
  const siteUrl = config.public.siteUrl

  // No `title`: the home page uses the full brand title (seo.defaultTitle).
  useSiteSeo(() => ({
    description: t('seo.home.description'),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      name: t('seo.defaultTitle'),
      url: `${siteUrl}/`,
      description: t('seo.home.description'),
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      primaryImageOfPage: `${siteUrl}/og-image.png`,
    },
  }))

const number = ref(0)
const section = ref(null)
const threeSection = ref(null)
const showThree = ref(false)
let threeObserver

onMounted(async () => {
  threeObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return
      showThree.value = true
      threeObserver.disconnect()
    },
    { rootMargin: '600px 0px' },
  )
  threeObserver.observe(threeSection.value)

  const { gsap } = await loadGsap()
  gsap.to(number, {
    scrollTrigger: {
      trigger: section.value,
      start: 'top center',
      toggleActions: 'play none none none',
    },
    duration: 2,
    value: 107,
    ease: 'power4.inOut',
    onUpdate: () => {
      number.value = Math.round(gsap.getProperty(number, 'value'))
    },
  })
})

onBeforeUnmount(() => threeObserver?.disconnect())
  </script>
  